/**
 * MONSTER TOWN — THE COLLECTION BOOK. FROZEN CONTRACT (src/core/*).
 * Additive only.
 *
 * One registry for the four things a kid can discover: FISH, BUGS, RECIPES and
 * ANIMALS. Each entry declares WHERE and WHEN it can be found, as data, and the
 * registry answers "what is out right now, here" — which is the whole payoff of
 * the day/night cycle: morning butterflies, evening fireflies, night beetles,
 * pond fish versus river fish.
 *
 * CONTENT IS A DATA DROP. A new creature is an entry in `src/content/creatures.ts`
 * (or `recipes.ts` / `animals.ts`) and nothing else. This file never names a
 * species, a pond, or a habitat.
 *
 * LAWS THIS FILE HOLDS:
 *  - Nothing is ever unfindable. `rarity` is clamped to a floor above zero, and
 *    an entry with no availability rule at all is available everywhere, always.
 *  - Finding something is only ever a gain. `found()` cannot return a loss, a
 *    duplicate is still a happy moment (and still counts), and there is no
 *    "wrong" catch.
 *  - A page is a PICTURE (LAW #4). `devName` is dev-only and never shown.
 */

import './dayEvents'
import { bus } from './events'
import { clock, type TimePhaseId } from './time'

export type CollectionKind = 'fish' | 'bug' | 'recipe' | 'animal'

/**
 * When and where something can be found. All fields are optional: an empty rule
 * means "anywhere, any time", which is the right default for a recipe.
 */
export interface AvailabilityRule {
  /** Bands it appears in. Undefined = every band. */
  readonly phases?: readonly TimePhaseId[]
  /** Hour window [from, to). Wraps midnight. Undefined = all day. */
  readonly fromHour?: number
  readonly toHour?: number
  /**
   * PLACE TAGS the world/venue crews stamp on a spot: 'pond', 'river', 'grass',
   * 'tree', 'garden', 'zoo', 'kitchen'... Data strings, never interpreted here.
   * Undefined = anywhere.
   */
  readonly places?: readonly string[]
  /** Not before this in-world day. Gentle pacing, never a lock (LAW #2). */
  readonly afterDay?: number
  /** Relative chance 0..1 when several things are available. Floored at 0.05. */
  readonly rarity?: number
}

/** What an animal wants — the zoo's whole learning payload, as data. */
export interface CreatureLikes {
  readonly food?: readonly string[]
  readonly habitat?: readonly string[]
  /** Audio cue id. */
  readonly sound?: string
}

export interface CollectionEntry {
  readonly id: string
  readonly kind: CollectionKind
  /** DEV ONLY. Never shown to a kid (LAW #4). */
  readonly devName: string
  /** Icon key for the book page and the HUD. */
  readonly icon: string
  /** Optional 2D art for the big book page. Falls back to the icon. */
  readonly draw?: (c: CanvasRenderingContext2D, size: number) => void
  /** Non-empty by construction: something must say where it lives. */
  readonly where: readonly [AvailabilityRule, ...AvailabilityRule[]]
  readonly tags?: readonly string[]
  readonly likes?: CreatureLikes
  /** Coins the first find is worth. Always a gain, never a cost. */
  readonly coins?: number
  /** Item id this becomes in the bag when caught. Defaults to `id`. */
  readonly itemId?: string
}

/** Authoring helper — mirrors `definePlaceable`. */
export function defineCollectible(e: CollectionEntry): CollectionEntry {
  return e
}

const ENTRIES = new Map<string, CollectionEntry>()

export function registerCollectible(e: CollectionEntry): CollectionEntry {
  ENTRIES.set(e.id, e)
  return e
}

export function registerCollectibles(list: readonly CollectionEntry[]): void {
  for (const e of list) registerCollectible(e)
}

export function getCollectible(id: string): CollectionEntry | undefined {
  return ENTRIES.get(id)
}

export function allCollectibles(): CollectionEntry[] {
  return [...ENTRIES.values()]
}

export function collectiblesOfKind(kind: CollectionKind): CollectionEntry[] {
  return [...ENTRIES.values()].filter((e) => e.kind === kind)
}

// ---------------------------------------------------------------------------
// availability — pure, testable, no globals in the signature
// ---------------------------------------------------------------------------

export interface AvailabilityQuery {
  readonly hour: number
  readonly phase: TimePhaseId
  readonly day: number
  /** The place tag under the finger. Undefined matches rules with no `places`. */
  readonly place?: string
}

function hourInWindow(hour: number, from: number | undefined, to: number | undefined): boolean {
  if (from === undefined && to === undefined) return true
  const f = ((from ?? 0) % 24 + 24) % 24
  const t = ((to ?? 24) % 24 + 24) % 24
  const h = ((hour % 24) + 24) % 24
  if (f === t) return true
  return f < t ? h >= f && h < t : h >= f || h < t
}

/** Does one rule admit this query? */
export function ruleAdmits(rule: AvailabilityRule, q: AvailabilityQuery): boolean {
  if (rule.phases !== undefined && !rule.phases.includes(q.phase)) return false
  if (!hourInWindow(q.hour, rule.fromHour, rule.toHour)) return false
  if (rule.afterDay !== undefined && q.day < rule.afterDay) return false
  if (rule.places !== undefined) {
    if (q.place === undefined) return false
    if (!rule.places.includes(q.place)) return false
  }
  return true
}

/** True if ANY of the entry's rules admit the query. Pure. */
export function isAvailable(id: string, q: AvailabilityQuery): boolean {
  const e = ENTRIES.get(id)
  if (e === undefined) return false
  return e.where.some((r) => ruleAdmits(r, q))
}

/** The current query, straight off the clock. */
export function nowQuery(place?: string): AvailabilityQuery {
  const s = clock.now()
  return place === undefined
    ? { hour: s.hour, phase: s.phase, day: s.day }
    : { hour: s.hour, phase: s.phase, day: s.day, place }
}

/** Everything of a kind that could be found right now at `place`. */
export function availableNow(kind: CollectionKind, place?: string): CollectionEntry[] {
  const q = nowQuery(place)
  return collectiblesOfKind(kind).filter((e) => e.where.some((r) => ruleAdmits(r, q)))
}

/**
 * Pick one, weighted by rarity, using a caller-supplied rng so the result is
 * reproducible under `engine.seed()`. Returns null only when the list is empty —
 * callers should then widen the place, never tell the kid "nothing is here".
 */
export function rollAvailable(
  kind: CollectionKind,
  rng: () => number,
  place?: string,
): CollectionEntry | null {
  const q = nowQuery(place)
  const pool = collectiblesOfKind(kind).filter((e) => e.where.some((r) => ruleAdmits(r, q)))
  if (pool.length === 0) return null
  const weight = (e: CollectionEntry): number => {
    const admitting = e.where.filter((r) => ruleAdmits(r, q))
    let best = 0
    for (const r of admitting) best = Math.max(best, Math.max(0.05, r.rarity ?? 1))
    return best
  }
  const total = pool.reduce((s, e) => s + weight(e), 0)
  let roll = rng() * total
  for (const e of pool) {
    roll -= weight(e)
    if (roll <= 0) return e
  }
  return pool[pool.length - 1] ?? null
}

// ---------------------------------------------------------------------------
// what the kid has found
// ---------------------------------------------------------------------------

export interface FoundRecord {
  /** How many times. A duplicate is still a happy moment. */
  count: number
  /** In-world day of the first find, for the book page. */
  firstDay: number
}

export interface CollectionSaveState {
  found: Record<string, FoundRecord>
}

export interface BookRow {
  readonly entry: CollectionEntry
  readonly found: boolean
  readonly count: number
  readonly firstDay: number
}

class CollectionBook {
  private records = new Map<string, FoundRecord>()

  has(id: string): boolean {
    return (this.records.get(id)?.count ?? 0) > 0
  }
  count(id: string): number {
    return this.records.get(id)?.count ?? 0
  }
  foundIds(): string[] {
    return [...this.records.keys()].filter((k) => this.has(k)).sort()
  }

  /**
   * The kid found something. Always a gain. `firstTime` drives the big "new
   * page!" moment; a repeat is still worth a small chime and still counts up.
   */
  found(id: string, opts: { at?: { x: number; y: number; z: number } } = {}): {
    firstTime: boolean
    count: number
    coins: number
    entry: CollectionEntry | null
  } {
    const entry = ENTRIES.get(id) ?? null
    const prev = this.records.get(id)
    const firstTime = prev === undefined || prev.count === 0
    const rec: FoundRecord = {
      count: (prev?.count ?? 0) + 1,
      firstDay: firstTime ? clock.day : (prev?.firstDay ?? clock.day),
    }
    this.records.set(id, rec)
    const coins = firstTime ? Math.max(0, entry?.coins ?? 0) : 0
    bus.emit('collection:found', {
      id,
      kind: entry?.kind ?? 'fish',
      firstTime,
      count: rec.count,
      coins,
      ...(opts.at === undefined ? {} : { at: opts.at }),
    })
    return { firstTime, count: rec.count, coins, entry }
  }

  /** QA / migration hook: set a record without firing the discovery moment. */
  setFound(id: string, count: number, firstDay = 1): void {
    const n = Math.max(0, Math.round(count))
    if (n === 0) this.records.delete(id)
    else this.records.set(id, { count: n, firstDay: Math.max(1, Math.round(firstDay)) })
  }

  /** A whole book page, in registration order. Every entry, found or not. */
  pageOf(kind: CollectionKind): BookRow[] {
    return collectiblesOfKind(kind).map((entry) => {
      const r = this.records.get(entry.id)
      return {
        entry,
        found: (r?.count ?? 0) > 0,
        count: r?.count ?? 0,
        firstDay: r?.firstDay ?? 0,
      }
    })
  }

  completion(kind: CollectionKind): { found: number; total: number } {
    const rows = this.pageOf(kind)
    return { found: rows.filter((r) => r.found).length, total: rows.length }
  }

  snapshot(): CollectionSaveState {
    const found: Record<string, FoundRecord> = {}
    for (const k of [...this.records.keys()].sort()) {
      const r = this.records.get(k)
      if (r !== undefined && r.count > 0) found[k] = { count: r.count, firstDay: r.firstDay }
    }
    return { found }
  }

  restore(state: CollectionSaveState): void {
    this.records.clear()
    for (const [k, v] of Object.entries(state.found ?? {})) {
      if (typeof k !== 'string' || k === '') continue
      const count = Math.round(Number((v as FoundRecord)?.count))
      const firstDay = Math.round(Number((v as FoundRecord)?.firstDay))
      if (Number.isFinite(count) && count > 0) {
        this.records.set(k, { count, firstDay: Number.isFinite(firstDay) ? Math.max(1, firstDay) : 1 })
      }
    }
  }

  resetForTest(): void {
    this.records.clear()
  }
}

export const collection = new CollectionBook()

/** Test hook: empty the registry. Never called by the game. */
export function clearCollectibleRegistryForTest(): void {
  ENTRIES.clear()
}
